import React, { useState } from "react";
import {
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Box,
  InputAdornment,
} from "@mui/material";
import { Navigate } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import SecurityIcon from "@mui/icons-material/Security";
import ShieldIcon from "@mui/icons-material/Shield";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import PhoneCallbackIcon from "@mui/icons-material/PhoneCallback";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:4000";

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [redirect, setRedirect] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${BACKEND_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("email", email);
        localStorage.setItem("role", data.role || "user");
        onLogin(data.token, email);
        setRedirect(true);
      } else {
        setError(data.error || "Login failed");
      }
    } catch {
      setError("Failed to connect to server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (redirect) return <Navigate to="/" />;

  const features = [
    { icon: <PhoneCallbackIcon />, title: "Voice Simulation", desc: "Create realistic vishing call flows with AI" },
    { icon: <ShieldIcon />, title: "Real-time Analytics", desc: "Track call outcomes, capture data, and export reports" },
    { icon: <VerifiedUserIcon />, title: "Security Training", desc: "Train employees to recognize social engineering" },
  ];

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", width: "100vw" }}>
      {/* Left — Branding panel */}
      <Box
        sx={{
          flex: "0 0 45%",
          background: "#0f172a",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "center",
          p: 6,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)",
            top: -100,
            right: -150,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(139,92,246,0.1) 0%, transparent 70%)",
            bottom: -80,
            left: -100,
          },
        }}
      >
        <Box sx={{ position: "relative", zIndex: 1, maxWidth: 440 }}>
          {/* Logo */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 6 }}>
            <Box
              sx={{
                width: 44, height: 44, borderRadius: "12px",
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <SecurityIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ color: "#f8fafc", fontWeight: 700, fontSize: "1.4rem", lineHeight: 1.2 }}>
                VishTrap
              </Typography>
              <Typography sx={{ color: "#475569", fontSize: "0.7rem", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                Security Platform
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ color: "#f8fafc", fontWeight: 700, fontSize: "2rem", lineHeight: 1.3, letterSpacing: "-0.02em", mb: 2 }}>
            Enterprise Vishing
            <br />
            Simulation Platform
          </Typography>
          <Typography sx={{ color: "#94a3b8", fontSize: "1rem", lineHeight: 1.7, mb: 5 }}>
            Simulate, detect, and defend against voice phishing attacks.
            Protect your organization with AI-powered call flow simulations.
          </Typography>

          {/* Feature cards */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {features.map((f, i) => (
              <Box key={i} sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                <Box
                  sx={{
                    width: 40, height: 40, minWidth: 40, borderRadius: "10px",
                    background: "rgba(99,102,241,0.12)", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    color: "#818cf8",
                  }}
                >
                  {f.icon}
                </Box>
                <Box>
                  <Typography sx={{ color: "#e2e8f0", fontWeight: 600, fontSize: "0.9rem", mb: 0.3 }}>
                    {f.title}
                  </Typography>
                  <Typography sx={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.5 }}>
                    {f.desc}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* Right — Login form */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: "#ffffff",
          p: { xs: 3, sm: 6 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1, mb: 4 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <SecurityIcon sx={{ color: "#fff", fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#0f172a" }}>VishTrap</Typography>
          </Box>

          <Typography sx={{ fontWeight: 700, fontSize: "1.5rem", color: "#0f172a", mb: 0.5, letterSpacing: "-0.01em" }}>
            Welcome back
          </Typography>
          <Typography sx={{ color: "#64748b", fontSize: "0.9rem", mb: 4 }}>
            Sign in to your account to continue
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: "10px" }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              <Box>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155", mb: 0.8 }}>
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#334155", mb: 0.8 }}>
                  Password
                </Typography>
                <TextField
                  fullWidth
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#94a3b8", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading || !email || !password}
                sx={{
                  mt: 1,
                  py: 1.5,
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  borderRadius: "10px",
                  background: "#6366f1",
                  "&:hover": { background: "#4f46e5" },
                  "&.Mui-disabled": { background: "#6366f1", opacity: 0.6, color: "#fff" },
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                    <span>Signing in...</span>
                  </Box>
                ) : (
                  "Sign In"
                )}
              </Button>
            </Box>
          </form>

          <Typography sx={{ color: "#94a3b8", fontSize: "0.75rem", textAlign: "center", mt: 4 }}>
            VishTrap © 2026 · Enterprise Security Platform
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
